import { useState } from 'react';
import axios from 'axios';
import { Droplets, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { API_URL } from './constants';

const BRISTOL = ['1 - Hard lumps', '2 - Lumpy sausage', '3 - Cracked sausage', '4 - Smooth (normal)', '5 - Soft blobs', '6 - Mushy', '7 - Watery'];

export function OutputTab({ outputs, clientId, onRefresh, user }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ type: 'bowel', bristol_scale: '', colour: '', amount: 'moderate', blood_present: false, pain: false, notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, client_id: clientId };
      if (form.bristol_scale) payload.bristol_scale = parseInt(form.bristol_scale);
      await axios.post(`${API_URL}/api/clinical/output`, payload, { withCredentials: true });
      toast.success(`${form.type === 'bowel' ? 'Bowel' : 'Urine'} log recorded`);
      setDialogOpen(false);
      setForm({ type: 'bowel', bristol_scale: '', colour: '', amount: 'moderate', blood_present: false, pain: false, notes: '' });
      onRefresh();
    } catch (err) { toast.error('Failed'); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-white" onClick={() => setDialogOpen(true)} data-testid="add-output-btn"><Plus className="w-4 h-4 mr-1.5" />Log Output</Button>
      </div>

      {outputs.length === 0 ? (
        <Card className="glass-card border-border"><CardContent className="p-8 text-center"><Droplets className="w-12 h-12 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No output logs recorded</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {outputs.map(o => (
            <Card key={o.id} className="border border-[#F59E0B]/20" data-testid={`output-${o.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`p-2 rounded-lg ${o.type === 'bowel' ? 'bg-amber-500/10' : 'bg-blue-500/10'}`}>
                  <Droplets className={`w-5 h-5 ${o.type === 'bowel' ? 'text-amber-500' : 'text-blue-500'}`} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground capitalize">{o.type} — {o.amount}</p>
                  <p className="text-xs text-muted-foreground">
                    {o.type === 'bowel' && o.bristol_scale && `Bristol ${o.bristol_scale} · `}
                    {o.colour && `${o.colour} · `}
                    {o.blood_present && 'Blood present · '}
                    {o.pain && 'Pain reported'}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {o.recorded_by_name} ({o.staff_number || o.usic}) — {new Date(o.timestamp).toLocaleString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-card border-border sm:max-w-md">
          <DialogHeader><DialogTitle className="text-foreground">Log Bowel/Urine Output</DialogTitle><DialogDescription className="text-muted-foreground">Record output observations</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-foreground">Type</Label>
                <select value={form.type} onChange={e => setForm(p => ({...p, type: e.target.value}))} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="output-type">
                  <option value="bowel">Bowel</option><option value="urine">Urine</option>
                </select></div>
              <div><Label className="text-foreground">Amount</Label>
                <select value={form.amount} onChange={e => setForm(p => ({...p, amount: e.target.value}))} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="output-amount">
                  <option value="small">Small</option><option value="moderate">Moderate</option><option value="large">Large</option>
                </select></div>
            </div>
            {form.type === 'bowel' && (
              <div><Label className="text-foreground">Bristol Stool Scale</Label>
                <select value={form.bristol_scale} onChange={e => setForm(p => ({...p, bristol_scale: e.target.value}))} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="output-bristol">
                  <option value="">Select...</option>
                  {BRISTOL.map((b, i) => <option key={i+1} value={i+1}>{b}</option>)}
                </select></div>
            )}
            <div><Label className="text-foreground">Colour</Label><Input value={form.colour} onChange={e => setForm(p => ({...p, colour: e.target.value}))} placeholder="e.g., brown, dark, pale" className="mt-1 bg-muted border-border text-foreground" /></div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-foreground"><input type="checkbox" checked={form.blood_present} onChange={e => setForm(p => ({...p, blood_present: e.target.checked}))} className="rounded" />Blood present</label>
              <label className="flex items-center gap-2 text-sm text-foreground"><input type="checkbox" checked={form.pain} onChange={e => setForm(p => ({...p, pain: e.target.checked}))} className="rounded" />Pain reported</label>
            </div>
            <div><Label className="text-foreground">Notes</Label><Input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="mt-1 bg-muted border-border text-foreground" /></div>
            <Button className="w-full bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-white" onClick={handleAdd} disabled={submitting} data-testid="submit-output-btn">{submitting ? 'Recording...' : 'Record & Sign'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
